/*
 * The button.
 *
 * Three decisions in here are worth reading before changing anything.
 *
 * Loading keeps the label in place at zero opacity and lays the spinner over
 * it. Swapping the label for a spinner collapses the button to the spinner's
 * width, and everything to the right of it jumps sideways at the exact moment
 * the person is waiting to see whether their action worked. Zero opacity rather
 * than `visibility: hidden` because visibility removes the element from the
 * accessibility tree, and the button would lose its accessible name at the
 * precise moment a screen reader is announcing that it is busy.
 *
 * Loading does not set the native `disabled` attribute. A disabled button is
 * removed from the tab order, so the keyboard user who just pressed Enter on it
 * has their focus dropped to the top of the document and has to tab back
 * through the whole form to find out what happened. `aria-disabled` with the
 * click swallowed keeps them where they are. The `disabled` prop still uses the
 * native attribute, because that means something different: not available,
 * rather than working on it.
 *
 * Icon-only requires `aria-label` through the type system. Every icon-only
 * button in every codebase eventually ships without a name, because the rule
 * lives in a document rather than in the compiler; here the two shapes are a
 * discriminated union and the nameless one does not typecheck.
 */

import type { ButtonHTMLAttributes, MouseEvent, ReactNode } from 'react'
import { Spinner } from '../Spinner/Spinner'
import styles from './Button.module.css'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg'

interface ButtonBaseProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonVariant
  size?: ButtonSize
  /** In flight. Keeps the button's width, its focus and its accessible name. */
  loading?: boolean
  /** A glyph before the label. Never instead of it. */
  iconLeading?: ReactNode
  iconTrailing?: ReactNode
  fullWidth?: boolean
}

export type ButtonProps =
  | (ButtonBaseProps & { iconOnly?: false; children: ReactNode })
  | (ButtonBaseProps & { iconOnly: true; children: ReactNode; 'aria-label': string })

export function Button(props: ButtonProps) {
  const {
    variant = 'secondary',
    size = 'md',
    loading = false,
    iconLeading,
    iconTrailing,
    fullWidth = false,
    iconOnly = false,
    children,
    className,
    onClick,
    type = 'button',
    ...rest
  } = props

  const buttonClassName = [
    styles.button,
    styles[variant],
    size === 'sm' ? styles.sm : '',
    size === 'lg' ? styles.lg : '',
    iconOnly ? styles.iconOnly : '',
    fullWidth ? styles.fullWidth : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  function handleClick(event: MouseEvent<HTMLButtonElement>) {
    /*
     * The guard the native `disabled` attribute would have given us. Without
     * it a double press on a slow connection sends the broadcast twice, and a
     * broadcast is spoken to every member of a project.
     */
    if (loading || rest['aria-disabled'] === true || rest['aria-disabled'] === 'true') {
      event.preventDefault()
      return
    }
    onClick?.(event)
  }

  return (
    <button
      {...rest}
      type={type}
      className={buttonClassName}
      onClick={handleClick}
      aria-busy={loading || undefined}
      /*
       * The caller's own `aria-disabled` survives, which the obvious spelling
       * of this line did not: written as `loading || undefined` after the
       * spread, it overwrote a passed-in `aria-disabled` with `undefined` on
       * every render where nothing was in flight. The screen that found it was
       * the case referral gate, which has to say "not until a consent record
       * exists" while staying in the tab order, and had to fall back to the
       * native `disabled` attribute, which takes the control out of it.
       */
      aria-disabled={loading || rest['aria-disabled'] || undefined}
    >
      <span className={[styles.content, loading ? styles.contentLoading : ''].filter(Boolean).join(' ')}>
        {iconLeading}
        {iconOnly ? children : <span className={styles.label}>{children}</span>}
        {iconTrailing}
      </span>
      {loading ? (
        <span className={styles.loader}>
          <Spinner size={size === 'lg' ? 'md' : 'sm'} />
        </span>
      ) : null}
    </button>
  )
}
